import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import Porter from 'porter'

import constants from 'scoreboard/components/constants'
import LinesAndBands from './analytics/lines-and-bands'
import template from './chart-designer-editor.hbs'
import LeftAxisPanel from './left-axis-panel'
import RightAxisPanel from './right-axis-panel'
import XAxisPanel from './x-axis-panel'
import OtherSettingsPanel from './other-settings-panel'
import updatesNeeded from './updates-needed'
import { chart as isPolarChart } from 'scoreboard/charts/is-polar'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'

/**
 * ChartDesigner attributes that are simple arrays (instead of Backbone.Collection),
 * which need some special handling
 */
const DESIGNER_ARRAY_SETTINGS_ATTRIBUTES = [ 'colors', 'labels', 'legends' ]

/**
 * @typedef {Object} ChartDesignerEditorOptions
 * @property {Boolean} [hideTableToggle=false] Show/Hide the chart table
 * @property {ChartDesigner} model
 * @property {ChartData} data
 * @property {Number} [width] width of chart in the editor
 * @property {Number} [height] height of chart in the editor
 * @property {String} [submitLabel] What to display on the submit button
 * /

/**
 * Edit mode for Charts.
 *
 * #### Tips for Developers ####
 *
 * Anytime a new feature is add/removed from the collection `refreshPanels` will be invoked.
 * (e.g. this.model.features.push(newFeature)) You can set a breakpoint in refreshPanels
 * to see the full stack and where the event(s) originate.
 *
 * @type {any}
 */
const ChartDesignerEditor = Backbone.View.extend({
	className: 'chart-designer-editor',

	/** @type {ChartDesigner} */
	model: null,

	/** @type {ChartData} */
	data: null,

	events: {
		// Using mouseover insead of mouseenter because rapid mouse-movements can go
		// undetected. So if a mouseenter event is undetected, and the cursor is
		// inside the panel, the panel won't be able to open. But using mouseover
		// allows the event to get triggered when hovering over the element plus
		// its children so you get multiple attempts to trigger the event before the
		// mouse comes to a stop when it initially enters the panel. Issue #45374
		'mouseover .chart-designer-panel': 'onMouseOverChartDesignerPanel',
		'mouseleave .chart-designer-panel': 'onMouseLeaveChartDesignerPanel',
		'click': 'closeActivePanels'
	},

	/**
	 *
	 *
	 * @constructs
	 *
	 * @param {ChartDesignerEditorOptions} options
	 */
	initialize: function (options = {}) {
		this.options = options

		this.data = options.data

		this.leftAxisPanel = new LeftAxisPanel({
			model: this.model,
			data: this.data
		})

		// #37548 don't show the breakdown panel for hierarchical data,
		// tree maps, maps
		if (!this.model.isHierarchical()) {
			this.xAxisPanel = new XAxisPanel({model: this.model, data: this.data})
		}

		this.rightAxisPanel = new RightAxisPanel({
			model: this.model,
			data: this.data
		})
		this.otherSettingsPanel = new OtherSettingsPanel({
			model: this.model,
			data: this.data,
			hideTableToggle: this.options.hideTableToggle
		})

		this.leftAxisPanel.on('emphasize', featureId => this.emphasize(featureId))
		this.rightAxisPanel.on('emphasize', featureId => this.emphasize(featureId))

		this.listenTo(this.model, 'change', () => this.onChartUpdate())

		DESIGNER_ARRAY_SETTINGS_ATTRIBUTES.forEach(setting => {
			this.listenTo(this.otherSettingsPanel, `change:${setting}`, () => this.onIgnoredChange())
		})

		new Array(this.leftAxisPanel, this.rightAxisPanel, this.xAxisPanel).forEach(p => {
			if (!p) {
				return
			}

			this.listenTo(p, 'loading', () => this.trigger('loading'))
			this.listenTo(p, 'change:sortSettings', () => this.onIgnoredChange())
		})

		this.changeQueue = new ChangeQueue(this)
		this.isDragging = false
		this.panels = []
	},

	render: function () {
		this.$el.empty().append(template())
		this.panelContainer = this.$('.designer-panel-container')
		this.addPanel(this.leftAxisPanel)
		if (!this.model.isHierarchical()) {
			this.addPanel(this.xAxisPanel)
		}
		this.addPanel(this.rightAxisPanel)
		this.addPanel(this.otherSettingsPanel)
		this._setupPorter()

		this._toggleRightAxisPanelVisibility()

		this.on('resize', () => {
			this.leftAxisPanel.trigger('resize')
			if (!this.model.isHierarchical()) {
				this.xAxisPanel.trigger('resize')
			}
			this.rightAxisPanel.trigger('resize')
			this.otherSettingsPanel.trigger('resize')
		})

		this.setContainerHeight(true)

		return this
	},

	emphasize: function(featureId) {
		this.trigger('emphasize', featureId)
	},

	closeActivePanels(e) {
		if (_.isNil(e) || $(e.target).closest('.chart-designer-panel').length == 0) {
			for (const p of this.panels) {
				p.$el.removeClass('is-selected')
				this.hasActivePanel = false
			}
			this.setContainerHeight()
			return
		}
		if (!_.isNil(e) && !$(e.target).hasClass('sp-navigate') &&
			!this.settingContainerRenderedHeight) {
			this.setContainerHeight()
		}
	},

	_toggleRightAxisPanelVisibility: function() {
		let isHierarchical = this.model.isHierarchical()
		let rightAxisPanelHidden =
			nonAxesPlotType(this.model) || isPolarChart(this.model)

		this.$el.toggleClass('has-three-panels', rightAxisPanelHidden && !isHierarchical)
		this.$el.toggleClass('has-two-panels', isHierarchical)

		this.$(this.rightAxisPanel.el)
			.toggleClass('is-not-displayed', rightAxisPanelHidden)
	},

	/**
	 * Returns an array of DOMNodes that will be used as porter destination (can be DNDed)
	 * @private
	 */
	_porterDestinations: function () {
		return _(this._dndPanels()).map(panel => panel.porterDestinations().toArray())
			.flatten().value()
	},

	_dndPanels: function() {
		return [this.leftAxisPanel, this.rightAxisPanel]
	},

	_setupPorter: function () {
		this.porter = new Porter(this._porterDestinations(), {
			accept: (args) => {
				return Porter.Delivery.MOVE
			},
			//normally porter handles this, but because of the complicated interactions
			//with the series views, we'll handle what we need to do
			appendOnAcceptance: false,
			refreshOnEnd: false,
			dragThreshold: constants.DRAG_THRESHOLD_MS
		})

		//note cannot use multiple events here because this is a node listener
		//https://github.com/nodejs/node/issues/15697
		this.porter.on('enter-destination', this._onDragOverNode.bind(this))
		this.porter.on('over-destination', this._onDragOverNode.bind(this))
		this.porter.on('exit-destination', this._onDragExitNode.bind(this))
		this.porter.on('start', this._onDragStart.bind(this))
		this.porter.on('end', this._onDragEnd.bind(this))
		this.porter.on('drag', this._onDrag.bind(this))
	},

	_onDrag: function(selectedDestination) {
		// #44068 if dragging over nothing, show the drophints at the bottom of
		// the panels
		_.isNil(selectedDestination) && _.invokeMap(this._dndPanels(),
			'toggleDropHint', true)
	},

	_onDragStart: function({sourcePackage, destination}) {
		let featureEl = $(sourcePackage)
		let featureModel = featureEl[0].featureModel
		let featureAxis = featureModel.get('axis')
		let axis = featureAxis === 'right' ? this.rightAxisPanel : this.leftAxisPanel
		let otherAxis = featureAxis === 'right' ? this.leftAxisPanel : this.rightAxisPanel

		let activeFeatureViews = axis.activeFeatureViews
		let parentId = featureModel.get('parentId') ||
			featureModel.get('tempParentId')
		if (!_.isNil(parentId)) {
			let parent = _.find(activeFeatureViews, v =>
				v.model.get('id') == parentId ||
				v.model.get('tempId') == parentId)
			activeFeatureViews = parent.serieViews
		}
		let featureView = _.find(activeFeatureViews,
			v => v.model == featureModel)

		//signal drag event on the node, the serie-view and parent view will
		//update accordingly to show the correct effects while a node is dragging
		featureEl.trigger('drag', true).prev('.dropzone').addClass('drop')

		//when dragging, make both panels active and show the drop hint
		_.invokeMap(this._dndPanels(), 'toggleActive', true)
		otherAxis.toggleDropHint(true)

		// we want the laying of shadows trigger on the series inside of the
		// stack so filter out stack elements from the porter so they are
		// not recognized
		let porterDestinations = _.filter(this._porterDestinations(),
			dest => !$(dest).hasClass('stack'))
		this.porter.refresh(porterDestinations)
		this.isDragging = true
		featureView.displayTemporaryDropzone()

		// Match the heights of the dropzones to the height of the
		// `.porter-delivery` element
		let draggedNodeHeight = $(sourcePackage.delivery).outerHeight()
		this.$('.dropzone').height(draggedNodeHeight)
	},

	_onDragEnd: function({sourcePackage, destination, result}) {
		this.porter.refresh(this._porterDestinations())

		// Remove the active state of the panel that wasn't the drop destination
		let activePanel = this.$('.is-selected').has(destination)
		this.$('.is-selected').not(activePanel).removeClass('is-selected')

		// hide all dropzones when done dragging
		$('.dropzone').removeClass('is-selected temporarily-active is-not-displayed')
			.height('') // Remove all fixed heights when nothing is dragged
		this.isDragging = false

		$(sourcePackage).trigger('drag', false)
		_.invokeMap(this._dndPanels(), 'toggleDropHint', false)

		if (result == Porter.Delivery.MOVE && destination.dropPosition) {
			if (!this.isValidDrop(sourcePackage, destination)) {
				return
			}
			this.updatePlotTypes(sourcePackage, destination)
			let newAxis = destination.axis || destination.featureModel.get('axis')
			let prevAxis = sourcePackage.featureModel.get('axis')
			let prevIndex = sourcePackage.featureModel.get('sortOrder')
			let newIndex = this.getDropIndex(sourcePackage, destination,
				prevAxis != newAxis)

			//do the actual reorder once porter is done, if dropPosition wasn't present
			//then node never moved
			delete destination.dropPosition

			//handle situation when reordering within the same axis
			let isDiffAxis = newAxis != prevAxis
			let newFeatures = this.model.featuresByAxis(newAxis)
			let prevFeatures = isDiffAxis ? this.model.featuresByAxis(prevAxis) : newFeatures

			this.model.setFeatureAxis(sourcePackage.featureModel, newAxis)

			this.model.reorderFeatures({
				sourcePackage,
				destination,
				prevFeatures,
				newFeatures,
				prevIndex,
				newIndex
			})

			if (sourcePackage.featureModel.get('type') === 'stack') {
				// this will also set the axes on all of the children, which
				// we will want to do if moving a stack
				sourcePackage.featureModel.reorderChildren()

				// we don't need to reorder plots if we moved an empty stack
				if (!sourcePackage.featureModel.get('children')) {
					return
				}
			}

			//due to the repaint, we must rebind the new painted nodes to
			//porter
			this.porter.refresh(this._porterDestinations())
		}
	},

	_onDragOverNode: function (domNode, position) {
		//as the drag moves through the destination, update where the drop
		//shadow should show
		domNode.dropPosition = position
		$(domNode).trigger('dropShadow', _.extend({show: true}, position))
	},

	_onDragExitNode: function (domNode) {
		//when the drag leaves a destination remove the corresponding drop shadow
		$(domNode).trigger('dropShadow', {show: false})
	},

	getDropIndex: function(sourcePackage, destination, diffAxes) {
		const prevIndex = sourcePackage.featureModel.get('sortOrder')
		if (_.isNil(destination.featureModel)) {
			return 0
		}
		let dropIndex = destination.featureModel.get('sortOrder')

		// find if we are placing over or under the model
		let placeOnBottom
		if (this.$(destination).hasClass('dropzone')) {
			let firstDropzone =
				this.$(destination).parent().find('.dropzone')[0]

			// if the dropzone is the first in its axis
			let isTopDropzone = firstDropzone == destination
			placeOnBottom = !isTopDropzone
		} else {
			placeOnBottom = !destination.dropPosition.top && diffAxes
		}

		// if the features have the same parent, whether that is an axis
		// or a parent feature
		let sameParent = (_.isNil(sourcePackage.parentFeatureModel)
		  && _.isNil(destination.parentFeatureModel)) ||
		  (sourcePackage.parentFeatureModel == destination.parentFeatureModel)

		// account for features below shifting up if we are placing further
		// down in same axis or parent
		let shiftingDown = (!diffAxes && sameParent) && prevIndex < dropIndex

		//if we're dropping at the bottom, put after the serie
		if (placeOnBottom && !shiftingDown) {
			dropIndex++
		}

		return dropIndex
	},

	isValidDrop: function(sourcePackage, destination) {
		const seriePackage = sourcePackage.featureModel.get('type') === 'serie'
		const droppingOnStack = !_.isNil(destination.parentFeatureModel)

		// if dragging a non-serie into a stack, do nothing
		// if plot types don't match, do nothing
		if (droppingOnStack && !seriePackage) {
			return false
		}

		// if placing the feature back down on its own dropzone
		return sourcePackage.featureModel != destination.featureModel
	},

	/**
	 * if dragging and dropping into a parent feature, make sure to correct any
	 * mismatching plottypes i.e. (dropping a bar serie into an area stack)
	 */
	updatePlotTypes: function(sourcePackage, destination) {
		const parentModel = destination.parentFeatureModel
		const sourceModel = sourcePackage.featureModel
		if (_.isNil(parentModel) || _.isNil(sourceModel)) return

		const parentPlotType = parentModel.get('plotType')
		if (parentPlotType != sourceModel.get('plotType')) {
			sourceModel.set('plotType', parentPlotType)
		}
	},

	addPanel: function (panel) {
		this.panels.push(panel)
		this.panelContainer.append(panel.el)
		panel.render()
	},

	onMouseOverChartDesignerPanel: function (e) {
		if (this.hasTooltipOrSelectMenu() ||
			// If the panel is already active, no need to continue
			$(e.currentTarget).hasClass('is-selected')) {
			return
		}

		let activeCid = $(e.currentTarget).data('cid')
		this.panels.forEach(p => {
			p.toggleActive(activeCid === p.cid || this.isActiveYAxisPanel(p.$el))
		})

		// we're placing new dropzones so refresh the porter destinations
		!this.isDragging && this.porter.refresh(this._porterDestinations())

		this.hasActivePanel = true
	},

	onMouseLeaveChartDesignerPanel: function (e) {
		let panelEl = this.$(e.currentTarget)

		// If we are dragging an item and leave a panel, we want to call
		// drophint again so that it appears at the bottom of the panel
		if (this.isActiveYAxisPanel(panelEl)) {
			return
		}

		if (this.hasTooltipOrSelectMenu()) return

		panelEl.removeClass('is-selected')
		this.hasActivePanel = false
		this.setContainerHeight()
	},

	hasTooltipOrSelectMenu: function() {
		// If a tooltip is present anywhere, we don't want to toggle active state
		// until mouseleave because the presence of the tooltip indicates its
		// attached to the active content.
		return $('.tooltip').length || this.$('.select.has-menu').length
	},

	isActiveYAxisPanel: function(panelEl) {
		// if there is an active drag and it's
		// a y axis panel, it should be active
		return panelEl.hasClass('y-axis-panel') && this.isDragging
	},

	/*
	 * Sets a fixed height on the panel container so the active states won't
	 * cause the chart to change positions. After the the panels are all inactive,
	 * this we'll call this method so the chart can reposition and take up as
	 * much room as possible.
	 */
	setContainerHeight: function(rendered) {
		this.settingContainerRenderedHeight = rendered

		// If called by mousing out of a panel, make sure another panel wasn't
		// activated immediately after.
		// Don't run any of this if either are true:
		// 1. There's an active panel
		// 2. The content reloaded from canceling the changes
		if (this.hasActivePanel || this.removed) return

		this.removed = false

		let tallestPanelHeight = 0
		this.previousTallestPanelHeight

		this.panels.forEach(panel => {
			let panelHeight = panel.$el.height()
			if (panelHeight > tallestPanelHeight) {
				tallestPanelHeight = panelHeight
			}
		})

		let totalHeight = tallestPanelHeight + constants.STANDARD_MARGIN
		// Have to set both height and min-height to make the transition work
		// when the tallest panel height gets shorter than it previously was.
		this.panelContainer.css({
			'height': `${totalHeight}px`,
			'min-height': `${totalHeight}px`
		})

		let isNewTallestPanelHeight =
			tallestPanelHeight !== this.previousTallestPanelHeight
		this.previousTallestPanelHeight = tallestPanelHeight
		// If method was called from intial rendered or if the tallest panel height
		// didn't change, stop here.
		if (this.settingContainerRenderedHeight || !isNewTallestPanelHeight) {
			this.settingContainerRenderedHeight = false
			return
		}

		// If rerendering the chart, we'll re-render the chart every few splt seconds
		// while the CSS height transitions for the panels take place. This allows
		// the chart to look like it's adapting to the space.
		let interval = setInterval(() => {
			this.trigger('rerenderChart')
		}, 10)

		// When the CSS transitions are complete, stop the re-rendering the chart
		setTimeout(() => {
			clearInterval(interval)
		}, constants.CHART_PANEL_TRANSITION_TIME)
	},

	onChartUpdate: function () {
		let results = updatesNeeded(this.model.attributes, this.model.previousAttributes(),
			IGNORE_CHANGE_SETTINGS)

		this.changeQueue.change(results)

		this._toggleRightAxisPanelVisibility()
	},

	onIgnoredChange: function () {
		// Need to listen for some manually triggered change events (like
		// change:sortSettings) instead of the model's event. We're ignoring
		// changes to properties like 'colors'/'labels' in ChartDesigner
		// because those might be changed with some server-generated defaults
		// after a fetch, and we don't want to get stuck in a change loop. So
		// instead, we explicitly listen for an event from the dialog views
		// themselves
		this.changeQueue.change({ fetch: true, rerender: true })
	},

	refreshPanels: function () {
		this.panels.forEach(p => {
			p.refresh()
		})
		this.porter.refresh(this._porterDestinations())
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ ...this.panels ], 'remove')
		this.removed = true
	}

})

/**
 * Queues ChartDesigner change events so that a single set of changes only results
 * in a single fetch/rerender. For example, a single UI action might update
 * multiple models, triggering change event handlers in the middle of updating.
 * This queues up those and triggers a single 'change' event on the provided
 * ChartDesignerEditor after a timeout.
 */
class ChangeQueue {
	constructor (editor) {
		this.editor = editor
		this.fetch = false
		this.rerender = false
	}

	change ({ fetch, rerender }) {
		// The previously queued fetch/rerender values should only be overridden
		// if adding a fetch or rerender

		if (fetch) {
			this.fetch = true
		}
		if (rerender) {
			this.rerender = true
		}

		this.countdown()
	}

	countdown () {
		if (!this.timeoutId) {
			// No need to wait long, just giving the event loop time to clear
			this.timeoutId = setTimeout(this.launch.bind(this), 0)
		}
	}

	launch () {
		delete this.timeoutId
		let options = _.pick(this, 'fetch', 'rerender')
		this.fetch = this.rerender = false
		this.editor.trigger('change', options)
	}
}

const IGNORE_CHANGE_SETTINGS = {
	// See explanation for ignoring 'colors', 'labels', and *sortSettings changes in onIgnoredChange()
	noRerender: DESIGNER_ARRAY_SETTINGS_ATTRIBUTES.concat([
		'leftAxisBarSortSettings', 'rightAxisBarSortSettings',
		'error', 'id', 'tempId', 'zoomTo'
	]),
	nested: {
		breakdowns: {
			// See explanation for ignoring 'sortSettings' changes in onIgnoredChange()
			noRerender: [ 'sortSettings', 'valueSuggestions', 'error', 'id'],
			nested: {
				ranges: {
					noRerender: ['id'],
					noFetch: [ 'id' ]
				}
			}
		},
		features: {
			noRerender: [ 'id', 'error' ],
			noFetch: [ 'color', 'label' ],
			nested: {
				periods: {
					noRerender: [ 'breakdownRangeId' ]
				}
			}
		}
	}
}

// features can have child features, and they have the same ignore settings
IGNORE_CHANGE_SETTINGS.nested.features.nested.children = _.cloneDeep(
	_.pick(IGNORE_CHANGE_SETTINGS.nested.features, 'noRerender', 'noFetch'))

// These properties are added client-side to child features in lines-and-bands.js,
// and changes to them should not trigger re-render or fetch
LinesAndBands.DECORATED_PROPERTIES.forEach(p => {
	IGNORE_CHANGE_SETTINGS.nested.features.nested.children.noRerender.push(p)
})

_.extend(ChartDesignerEditor, {
	IGNORE_CHANGE_SETTINGS,
	DESIGNER_ARRAY_SETTINGS_ATTRIBUTES
})
export default ChartDesignerEditor
export { IGNORE_CHANGE_SETTINGS }
export { DESIGNER_ARRAY_SETTINGS_ATTRIBUTES }

ChartDesignerEditor.__filename__ = 'charts/edit/chart-designer-editor.js'
